// Self-hosted face recognition using @vladmandic/face-api (MIT). Models served from /models.
import * as faceapi from "@vladmandic/face-api";

let _loading = null;
let _loaded = false;
let _liveCanvas = null;

export async function loadFaceModels() {
  if (_loaded) return;
  if (_loading) return _loading;
  const url = "/models";
  _loading = (async () => {
    // Load the light detector first so the live preview can start fast.
    await faceapi.nets.tinyFaceDetector.loadFromUri(url);
    await faceapi.nets.faceLandmark68Net.loadFromUri(url);
    await faceapi.nets.ssdMobilenetv1.loadFromUri(url);
    await faceapi.nets.faceRecognitionNet.loadFromUri(url);
    _loaded = true;
  })();
  return _loading;
}

export function modelsReady() {
  return _loaded;
}

/** Copy the current video frame onto a canvas. Android Chrome often cannot feed
 *  a hardware-decoded <video> into TensorFlow; a 2D canvas works reliably. */
export function snapshotVideo(video, { maxSide = 640, padRatio = 0, canvas = null } = {}) {
  const vw = video?.videoWidth || 0;
  const vh = video?.videoHeight || 0;
  if (!vw || !vh) return null;
  let dw = vw;
  let dh = vh;
  const longest = Math.max(vw, vh);
  if (longest > maxSide) {
    const scale = maxSide / longest;
    dw = Math.round(vw * scale);
    dh = Math.round(vh * scale);
  }
  const padX = Math.round(dw * padRatio);
  const padY = Math.round(dh * padRatio);
  const c = canvas || document.createElement("canvas");
  c.width = dw + padX * 2;
  c.height = dh + padY * 2;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  if (padRatio > 0) {
    ctx.fillStyle = "#6b7280";
    ctx.fillRect(0, 0, c.width, c.height);
  }
  ctx.drawImage(video, padX, padY, dw, dh);
  return c;
}

function liveCanvas() {
  if (!_liveCanvas) _liveCanvas = document.createElement("canvas");
  return _liveCanvas;
}

// FAST live preview: TinyFaceDetector + landmarks only (no descriptor).
export async function detectFacesLive(input) {
  const opts = new faceapi.TinyFaceDetectorOptions({ inputSize: 416, scoreThreshold: 0.2 });
  return faceapi.detectAllFaces(input, opts).withFaceLandmarks();
}

/** Live detect from a <video> via canvas (works on Android phones). */
export async function detectFacesLiveFromVideo(video) {
  const canvas = snapshotVideo(video, { maxSide: 416, padRatio: 0.2, canvas: liveCanvas() });
  if (!canvas) return [];
  try {
    return await detectFacesLive(canvas);
  } catch (e) {
    return [];
  }
}

// Accurate one-shot capture: SSD + landmarks + descriptor.
export async function detectFaces(input) {
  const ssd = new faceapi.SsdMobilenetv1Options({ minConfidence: 0.25 });
  let res = await faceapi.detectAllFaces(input, ssd).withFaceLandmarks().withFaceDescriptors();
  if (res.length) return res;
  const tiny = new faceapi.TinyFaceDetectorOptions({ inputSize: 416, scoreThreshold: 0.2 });
  return faceapi.detectAllFaces(input, tiny).withFaceLandmarks().withFaceDescriptors();
}

/** Capture-time detect from a <video>. Retries with padding because SSD/Tiny
 *  miss faces that fill most of a selfie frame (typical on phones). */
export async function detectFacesFromVideo(video) {
  const attempts = [
    { maxSide: 640, padRatio: 0.22 },
    { maxSide: 512, padRatio: 0.35 },
    { maxSide: 416, padRatio: 0 },
  ];
  for (const opts of attempts) {
    const canvas = snapshotVideo(video, opts);
    if (!canvas) continue;
    try {
      const res = await detectFaces(canvas);
      if (res.length) return res;
    } catch (e) {
      /* try next scale */
    }
  }
  return [];
}

// Eye Aspect Ratio for blink-based liveness
function dist(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}
function eyeEAR(p) {
  // p: 6 points
  return (dist(p[1], p[5]) + dist(p[2], p[4])) / (2 * dist(p[0], p[3]));
}
export function computeEAR(landmarks) {
  try {
    const pts = landmarks.positions;
    const left = [36, 37, 38, 39, 40, 41].map((i) => pts[i]);
    const right = [42, 43, 44, 45, 46, 47].map((i) => pts[i]);
    return (eyeEAR(left) + eyeEAR(right)) / 2;
  } catch (e) {
    return null;
  }
}

export function descriptorToArray(desc) {
  return Array.from(desc);
}

let _readLoading = null;

/** Detector + landmarks only, for the public hair consult preview. */
export async function loadFaceReadModels() {
  const tiny = faceapi.nets.tinyFaceDetector;
  const marks = faceapi.nets.faceLandmark68Net;
  if (tiny.isLoaded && marks.isLoaded) return;
  if (_readLoading) return _readLoading;
  const url = "/models";
  _readLoading = Promise.all([tiny.loadFromUri(url), marks.loadFromUri(url)]);
  return _readLoading;
}

/** Largest readable face in a still image. Falls back to SSD if the light detector misses. */
export async function detectFaceForReading(input) {
  await loadFaceReadModels();
  const tiny = new faceapi.TinyFaceDetectorOptions({ inputSize: 512, scoreThreshold: 0.25 });
  let res = await faceapi.detectAllFaces(input, tiny).withFaceLandmarks();
  if (!res.length) {
    if (!faceapi.nets.ssdMobilenetv1.isLoaded) {
      await faceapi.nets.ssdMobilenetv1.loadFromUri("/models");
    }
    const ssd = new faceapi.SsdMobilenetv1Options({ minConfidence: 0.25 });
    res = await faceapi.detectAllFaces(input, ssd).withFaceLandmarks();
  }
  return res;
}
